import { AuthApiError, type AuthApiErrorCode } from './authApi'

// Messages for errors from our own /api/auth endpoints. They are deliberately
// generic where the backend is (e.g. a wrong, expired or already-used code all
// read the same) so the UI never reveals more than the server does.
const API_ERROR_MESSAGES: Record<AuthApiErrorCode, string> = {
  AUTH_INVALID_REQUEST: 'Enter a valid email address.',
  AUTH_OTP_INVALID: 'That code is incorrect or has expired.',
  AUTH_OTP_LOCKED: 'Too many wrong codes. Request a new code.',
  AUTH_ACCOUNT_DISABLED: 'This account has been disabled.',
  AUTH_RATE_LIMITED: 'Too many attempts. Try again in a moment.',
  AUTH_METHOD_DISABLED: 'Email sign-in is unavailable right now. Try another method.',
  AUTH_EMAIL_UNAVAILABLE: "We couldn't send the email right now. Try again shortly.",
  AUTH_INVALID_TOKEN: 'Your session has expired. Sign in again.',
  AUTH_REAUTH_REQUIRED: 'For your security, sign out and sign back in, then try again.',
  AUTH_PASSKEY_INVALID: "That passkey didn't work. Try again, or use another way to sign in.",
  AUTH_PASSKEY_LIMIT: 'You have reached the maximum number of passkeys. Remove one to add another.',
  AUTH_PASSKEY_EXISTS: 'That passkey is already saved to your account.',
  AUTH_PASSKEY_NOT_FOUND: 'That passkey no longer exists.',
  AUTH_NETWORK: 'Network error - check your connection.',
  AUTH_UNKNOWN: 'Something went wrong. Please try again.',
}

// Errors that can still surface from Firebase Auth (Google sign-in, exchanging the
// custom token) and from the browser's passkey prompt (@simplewebauthn/browser's
// WebAuthnError codes). Keyed by the error's `code`.
const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-email': 'Enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/account-exists-with-different-credential':
    'An account with this email already exists. Sign in the way you did before.',
  'auth/too-many-requests': 'Too many attempts. Try again later.',
  'auth/network-request-failed': 'Network error - check your connection.',
  'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
  ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED: 'This device already has a passkey for your account.',
  ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT:
    'This device needs a screen lock (PIN, fingerprint or face) to use a passkey.',
  ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT:
    'This device or security key cannot store a passkey for sign-in.',
}

const formatWait = (seconds: number): string => {
  if (seconds < 90) return `${seconds} second${seconds === 1 ? '' : 's'}`
  return `${Math.ceil(seconds / 60)} minutes`
}

export const getAuthErrorMessage = (error: unknown): string => {
  if (error instanceof AuthApiError) {
    if (error.code === 'AUTH_RATE_LIMITED' && error.retryAfterSec) {
      return `Too many attempts. Try again in ${formatWait(error.retryAfterSec)}.`
    }
    return API_ERROR_MESSAGES[error.code]
  }

  const code = (error as { code?: string } | null)?.code
  if (code && FIREBASE_ERROR_MESSAGES[code]) {
    return FIREBASE_ERROR_MESSAGES[code]
  }
  return 'Something went wrong. Please try again.'
}
